/* eslint-disable react-hooks/rules-of-hooks */
import React from 'react'
import { useRouter } from 'next/router'
// Components
import Head from 'next/head'
import Loader from '../../components/newLoader/Loader'
import Header from '../../components/newHeader/Header'
import Steps from '../../components/puppy-travel/steps'
import CarouselPuppies from '../../components/puppy-travel/carouselPuppies'
import CustomerReview from '../../components/search/CustomerReview'
import Footer from '../../components/newFooter/newFooter'
// Styles
import styles from './index.module.scss'
import { ROBOTS_META } from 'const'

export default function index() {
  const router = useRouter()

  // Meta tags values
  const TITLE = 'Puppy Travel | MawooPets.com'
  const DESCRIPTION =
    'Discover how Mawoo Pets delivers puppies throughout the US and Canada. Whether by ground transport, flight nanny, or pet cargo, we have a travel solution just for you!'
  const IMAGE = `${process.env.NEXT_PUBLIC_ROOT_URL}/mawoo_social_1600x906.png`
  const URL = `${process.env.NEXT_PUBLIC_ROOT_URL}/puppy-travel`
  const CANONICAL = `${process.env.NEXT_PUBLIC_ROOT_URL}/puppy-travel`

  return (
    <>
      <Head>
        {/*  Facebook tags  */}
        <meta property="og:title" content={TITLE} key="title" />
        <meta name="og:description" content={DESCRIPTION} key="description" />
        <meta property="og:type" content="website" key="type" />
        <meta property="og:url" content={URL} key="url" />
        <meta property="og:image" content={IMAGE} key="image" />
        <meta property="og:image:alt" content="Search image" key="imageAlt" />
        <meta property="og:locale" content="en_US" key="locale" />
        {/*  twitter tags  */}
        <meta
          name="twitter:card"
          content="summary_large_image"
          key="twitterCard"
        />
        <meta name="twitter:title" content={TITLE} key="twitterTitle" />
        <meta
          name="twitter:description"
          content={DESCRIPTION}
          key="twitterDescription"
        />
        <meta name="twitter:image" content={IMAGE} key="twitterImage" />
        {/*  standard tags  */}
        <title>{TITLE}</title>
        <link rel="canonical" href={CANONICAL} />
        <meta name="description" content={DESCRIPTION} />
        <meta charSet="UTF-8" />

        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, user-scalable=0"
        />
        <meta name="robots" content={ROBOTS_META} />
      </Head>
      <main className={styles.main}>
        {router.isFallback && (
          <div className={styles.loader}>
            <Loader theme="tall" />
          </div>
        )}

        <Header />
        <div className={styles.content}>
          <section className={styles.header}>
            <h1>How do I get my puppy home?</h1>
            <p className={styles.signature}>
              Congratulations, you found the perfect puppy! Now, how do you
              bring the puppy home? <br /> Check out our options below!
            </p>
          </section>
          <div className={styles.bottomMobile}>
            <div className={styles.pickupBtn}>
              <div className={styles.iconMobile1} />
              <h2 className={styles.titleText}>
                <a href="#Pickup">Puppy Pickup</a>
              </h2>
            </div>
            <div className={styles.travelBtn}>
              <div className={styles.iconMobile2} />
              <h2 className={styles.titleText}>
                <a href="#Travel">Puppy Travel</a>
              </h2>
            </div>
          </div>
          <section className={styles.pickup} id="Pickup">
            <div className={styles.section1}>
              <div className={styles.subtitle}>
                <div className={styles.icon} />
                <h2>Puppy Pickup</h2>
              </div>
              <div className={styles.body}>
                <p className={styles.intro}>
                  Finding a local puppy can be a great option for an easy
                  stress-free pickup. You can even make a special day out of it
                  by visiting the breeder and checking out both your pup’s
                  siblings and their parents.
                </p>
              </div>
              <div className={styles.list}>
                <p className={styles.item}>
                  <div className={styles.check} />
                  <span>It’s free! </span> If your puppy is local or you are
                  willing to travel to pick up your pup.
                </p>
                <p className={styles.item}>
                  <div className={styles.check} />
                  Our team will <span>arrange a pickup date and time</span>{' '}
                  between you and the breeder.
                </p>
                <p className={styles.item}>
                  <div className={styles.check} />
                  We will also <span>share the breeder’s info </span> with you
                  so you can communicate without a problem and evacuate any
                  other questions.{' '}
                </p>
              </div>
            </div>

            <div className={styles.sectionImage}>
              <span className={styles.imageFamily} />
              <div className={styles.card}>
                <div className={styles.slide1}>
                  <h3 className={styles.number}>1,000+</h3>
                  <h3>Breeders</h3>
                </div>
                <div className={styles.slide2}>
                  <h3 className={styles.number}>43+</h3>
                  <h3>States & Provinces</h3>
                </div>
              </div>
            </div>
          </section>
          <section className={styles.travel} id="Travel">
            <div className={styles.section2}>
              <div className={styles._subtitle}>
                <div className={styles.icon2} />
                <h2>Puppy Travel</h2>
              </div>
              <div className={styles.body}>
                <p className={styles._intro}>
                  As you browse through our puppies for sale, you’ll notice that
                  perhaps your absolute favourite little dog doesn’t live
                  anywhere nearby. Sure, it’s technically easier to pick up a
                  new doggy from your own state or province, but you can’t help
                  who you fall in love with! That’s why our breeders are ready
                  to help organize safe transportation in a number of ways.
                </p>
              </div>
              <div className={styles.list}>
                <p className={styles._item}>
                  <div className={styles.check} />
                  Our team will{' '}
                  <span>
                    coordinate with the breeder and contact you for the best
                    travel options for your puppy.
                  </span>{' '}
                  We use ground transporters, flight nanny or pet cargo and you
                  will either receive your puppy at your doorstep or will have
                  to pick up at your nearest airport.
                </p>
                <p className={styles._item}>
                  <div className={styles.check} />
                  <span>The breeder’s contact information</span> will be shared
                  so you can communicate without a problem and evacuate any
                  other questions.{' '}
                </p>
                <p className={styles._item}>
                  <div className={styles.check} />
                  We will also <span>keep you updated</span> on the schedule and
                  any other info you might need throughout the travel.{' '}
                </p>
              </div>
            </div>
            <div className={styles.section3}>
              <span className={styles.imageTravel} />
              <div className={styles.card}>
                <div className={styles.slide1}>
                  <h3 className={styles.number}>500+</h3>
                  <h3>Puppies delivered</h3>
                </div>
                <div className={styles.slide2}>
                  <h3 className={styles.number}>200+</h3>
                  <h3>Cities delivered to</h3>
                </div>
              </div>

              <div className={styles.card2}>
                <h4>Get an estimate on your puppy travel</h4>
                <p className={styles.text}>
                  Just type your zip code or postal code into the travel cost
                  calculator on your puppy’s bio page. You can learn more by{' '}
                  <a
                    href="mailto:sales@mawoopets.com?subject=Inquiry%20regarding%20puppy%20delivery"
                    className={styles.btnChat}
                  >
                    getting in touch
                  </a>{' '}
                  with us directly.
                </p>
              </div>
            </div>
          </section>
          <Steps />

          <section className={styles.sectionCarousel}>
            <h2>Our adorable pups with their furever families!</h2>
            <p className={styles.signature}>
              Our flight nannies delivering pawfection to every state! We love
              seeing our puppies find their forever homes and get lots of love
              and snuggles!
            </p>
          </section>

          <div className={styles.containerCarousel}>
            <CarouselPuppies />
          </div>
          <div className={styles.componentCustomer}>
            <CustomerReview />
          </div>
        </div>
        <Footer />
      </main>
    </>
  )
}
